import { motion } from "framer-motion";
import RevenueEarnings from "../../components/dashboard/RevenueEarnings";
import AdminWelcomeSection from "../../components/dashboard/AdminWelcomeSection";
import SuperadminAnalytics from "../../components/dashboard/SuperadminAnalytics";
import DoctorsLeaderboard from "../../components/dashboard/DoctorsLeaderboard";
import AdminSpecialtyBreakdown from "../../components/dashboard/AdminSpecialtyBreakdown";
import AppointmentsTrend from "../../components/dashboard/AppointmentsTrend";
import RevenueByDoctor from "../../components/dashboard/RevenueByDoctor";
import { useDispatch, useSelector } from "react-redux";
import { getAdminById } from '../../redux/adminSlice';
import { docPatCreationCountryList, getallspecilitylist } from '../../redux/masterSlice';
import { useEffect, useRef, useState } from "react";
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import { useTranslation } from 'react-i18next';
import { useDashboardSocket } from '../../hooks/useDashboardSocket';
import CustomDatePicker from '../../components/dashboard/CustomDatePicker';
import SearchableSelect from '../../components/common/SearchableSelect';
import { Clock, CalendarDays, CalendarRange, Settings2, Stethoscope } from 'lucide-react';

// Booking-analytics ranges (today/week/month/custom) — same control + math as the
// doctor dashboard. Returns ISO start/end (null/null when a custom range isn't
// fully picked yet), so the resulting socket params stay stable across renders.
const getBookingRange = (rangeType, customStart, customEnd) => {
  const today = new Date();
  let startDate, endDate;
  switch (rangeType) {
    case 'today':
      startDate = new Date(today); startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today); endDate.setHours(23, 59, 59, 999);
      break;
    case 'week': {
      const currentDay = today.getDay();
      const daysToMonday = currentDay === 0 ? -6 : 1 - currentDay;
      startDate = new Date(today); startDate.setDate(today.getDate() + daysToMonday); startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate); endDate.setDate(startDate.getDate() + 6); endDate.setHours(23, 59, 59, 999);
      break;
    }
    case 'month':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1); startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); endDate.setHours(23, 59, 59, 999);
      break;
    case 'custom':
      if (customStart && customEnd) {
        startDate = new Date(customStart); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(customEnd); endDate.setHours(23, 59, 59, 999);
      }
      break;
    default:
      startDate = new Date(today); startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today); endDate.setHours(23, 59, 59, 999);
  }
  if (!startDate || !endDate) return { startDate: null, endDate: null };
  return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
};

function AdminDashboard() {
  const { t } = useTranslation('dashboard');
  const adminId = localStorage.getItem("ID");
  const userRole = localStorage.getItem("Role");
  const dispatch = useDispatch();
  const isInitialCall = useRef(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { admin, loading: isLoading, error } = useSelector((state) => state.admin);

  // Dashboard controls — owned here so the whole admin snapshot is driven by one
  // set of socket params. Children render the controls and call these setters.
  const [bookingRange, setBookingRange] = useState('week'); // booking analytics period (Week default)
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const selectedYear = new Date().getFullYear(); // current year only

  // Specialty filter — scopes the appointment metrics (bookings/revenue/leaderboard/
  // trend). The by-specialty chart stays showing all specialties.
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState('');
  const specialties = useSelector((state) => state.master?.specialties || []);
  useEffect(() => { dispatch(getallspecilitylist({})); }, [dispatch]);

  const handlePeriodChange = (period) => {
    if (period !== 'custom') {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
    setBookingRange(period);
  };

  // No global country filter — the admin view is platform-wide; the per-country
  // breakdown + selection lives inside RevenueEarnings (showAllCountries=true so
  // the snapshot always includes earningsByCountry).
  const countryId = null;
  const showAllCountries = true;
  const bookingDates = getBookingRange(bookingRange, customStartDate, customEndDate);

  // Single params object for the real-time admin snapshot. Values are
  // deterministic per (country, range, year), so JSON-serialization in the hook
  // stays stable across renders.
  const dashboardParams = {
    countryId,
    bookingStartDate: bookingDates.startDate,
    bookingEndDate: bookingDates.endDate,
    earningStartDate: `${selectedYear}-01-01T00:00:00.000Z`,
    earningEndDate: `${selectedYear}-12-31T23:59:59.999Z`,
    specialtyId: selectedSpecialtyId || null,
    showAllCountries,
  };

  const { data: dashboardData, loading: dashboardLoading } =
    useDashboardSocket('admin', dashboardParams, { enabled: userRole === 'admin' });

  useEffect(() => {
    if (isInitialCall.current) {
      if (userRole === 'admin' && adminId) {
        dispatch(getAdminById(adminId));
      }
      dispatch(docPatCreationCountryList());
      isInitialCall.current = false;
    }
  }, [dispatch, userRole, adminId]);

  // Set initial load to false after first render
  useEffect(() => {
    if (!isLoading && (admin || error)) {
      setIsInitialLoad(false);
    }
  }, [isLoading, admin, error]);

  // Handle loading state - only show loader on initial load
  if (isLoading && isInitialLoad) {
    return (
      <div data-testid="admin-dashboard-loading" className="bg-white rounded-2xl p-6 w-full h-full flex items-center justify-center shadow-lg">
        <div className="flex flex-col items-center justify-center gap-4 py-6">
          <CommonLoader />
          <span data-testid="admin-dashboard-loading-text" className="text-xl text-[#4ABDBD] font-semibold">{t('dashboard.loading')}</span>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error && !isLoading) {
    return <ErrorPage error={error} pageName={t('dashboard.admin_dashboard')} />;
  }

  if (!admin && !isLoading && !isInitialLoad) {
    return <ErrorPage error={{ message: t('no_admin_data') }} pageName={t('dashboard.admin_dashboard')} />;
  }

  if (isInitialLoad) {
    return (
      <div data-testid="admin-dashboard-initial-loading" className="bg-white rounded-2xl p-6 w-full h-full flex items-center justify-center shadow-lg">
        <div className="flex flex-col items-center justify-center gap-4 py-6">
          <CommonLoader />
          <span data-testid="admin-dashboard-initial-loading-text" className="text-xl text-[#4ABDBD] font-semibold">{t('dashboard.loading')}</span>
        </div>
      </div>
    );
  }

  // Booking-analytics period control — same UI as the doctor dashboard
  // (Today / Week / Month / Custom). Rendered inside the booking analytics card.
  const periodSelector = (
    <div data-testid="admin-dashboard-period-selector" className="flex flex-wrap items-center gap-2 sm:gap-3">
      <div data-testid="admin-dashboard-period-tabs" className="flex gap-1 bg-white p-1 rounded-2xl shadow border border-gray-200">
        {[
          { id: 'today', label: t('dashboard.time_ranges.today', 'Today'), icon: Clock },
          { id: 'week', label: t('dashboard.time_ranges.week', 'Week'), icon: CalendarDays },
          { id: 'month', label: t('dashboard.time_ranges.month', 'Month'), icon: CalendarRange },
          { id: 'custom', label: t('dashboard.time_ranges.custom', 'Custom'), icon: Settings2 },
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`admin-dashboard-period-tab-${tab.id}`}
            onClick={() => handlePeriodChange(tab.id)}
            className={`relative px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-300 z-10 flex items-center gap-1.5 ${bookingRange === tab.id ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
          >
            {bookingRange === tab.id && (
              <motion.div
                layoutId="activeAdminBookingPeriod"
                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
              />
            )}
            <tab.icon className="w-4 h-4 relative z-20" />
            <span className="relative z-20">{tab.label}</span>
          </button>
        ))}
      </div>
      {bookingRange === 'custom' && (
        <div data-testid="admin-dashboard-custom-date-range" className="flex items-center gap-2">
          <CustomDatePicker
            value={customStartDate || ''}
            onChange={(e) => setCustomStartDate(e.target.value)}
            maxDate={customEndDate || null}
            placeholder={t('dashboard.from_date', 'From')}
          />
          <span className="text-gray-400 text-sm">-</span>
          <CustomDatePicker
            value={customEndDate || ''}
            onChange={(e) => setCustomEndDate(e.target.value)}
            minDate={customStartDate || null}
            placeholder={t('dashboard.to_date', 'To')}
          />
        </div>
      )}
    </div>
  );

  return (
    <motion.div
      data-testid="admin-dashboard"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`${window.innerWidth <= 1024 ? 'p-4' : 'p-0'} w-full`}
    >
      {/* Header — row 1: title · row 2: period selector + specialty filter */}
      <div data-testid="admin-dashboard-header" className="mb-4 space-y-3">
        <h1 data-testid="admin-dashboard-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
          {t('dashboard.my_dashboard', 'My Dashboard')}
        </h1>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
          {periodSelector}
          <SearchableSelect
            testId="admin-dashboard-specialty-filter"
            icon={Stethoscope}
            value={selectedSpecialtyId || ''}
            onChange={(v) => setSelectedSpecialtyId(v)}
            placeholder={t('dashboard.all_specialties', 'All specialties')}
            searchPlaceholder={t('dashboard.search_specialty', 'Search specialty…')}
            options={specialties.map((s) => ({ value: s.specialtyId, label: s.name }))}
          />
        </div>
      </div>

      {/* Dashboard Content Container — uniform gap between every card */}
      <div data-testid="admin-dashboard-content" className="grid w-full gap-4">
        <AdminWelcomeSection
          counts={dashboardData?.counts}
          booking={dashboardData?.booking}
          loading={dashboardLoading}
        />
        <SuperadminAnalytics
          booking={dashboardData?.booking}
          earnings={dashboardData?.earnings}
          counts={dashboardData?.counts}
          leaderboard={dashboardData?.leaderboard}
          loading={dashboardLoading}
        />
        <RevenueEarnings
          earningsByCountry={dashboardData?.earningsByCountry}
          loading={dashboardLoading}
        />

        <DoctorsLeaderboard
          leaderboard={dashboardData?.leaderboard}
          loading={dashboardLoading}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 w-full gap-4">
          <RevenueByDoctor
            leaderboard={dashboardData?.leaderboard}
            loading={dashboardLoading}
          />
          <AdminSpecialtyBreakdown
            specialtyBreakdown={dashboardData?.specialtyBreakdown}
            loading={dashboardLoading}
          />
        </div>
        <AppointmentsTrend
          appointmentsTrend={dashboardData?.appointmentsTrend}
          loading={dashboardLoading}
        />
      </div>
    </motion.div>
  );
}

export default AdminDashboard;
